import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { envStrToBoolean, setBodyOverflow } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useIsModuleLoaded } from 'src/hooks/use-is-module-loaded';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import { setSelectedCategory } from 'src/store/app/actions';
import { RootState } from 'src/types/store-types';
import './styles.scss';

const Race: FC = () => {
  const moduleName = 'race';
  const isLoaded = useIsModuleLoaded(moduleName);
  const [fullScreen] = useState(false);
  const sessionRecorder = useSessionRecorder();
  const isFsRace = location.pathname.includes('fsRace') || location.pathname.includes('fs-race');

  const { user } = useSelector((state: RootState) => state.user);

  const navigate = useNavigate();

  const _setSelectedCategory = useConnectedAction(setSelectedCategory);

  useEffect(() => {
    sessionRecorder?.trackEvent('Race page view');
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    // Function to attempt starting the race widget
    const startWidget = (): any => {
      if (!window.__gdlib) {
        return <div>......</div>;
      }
      // call the startRaceWidget function with required parameters
      window.__gdlib.startRaceWidget({
        parentId: 'raceWidgetId', // ID for the widget's parent element
        customClassName: cn('frame', { ['fullScreen']: fullScreen }), // Custom class names for styling
        sdk: !envStrToBoolean(process.env.REACT_APP_RACE_FRAME),
        isFsRace: isFsRace || undefined,
      });
      return true; // Indicate that the widget has been started
    };

    // Attempt to start the widget immediately if already available
    if (startWidget())
      return () => {
        // Close the widget when the component is unmounted
        window.__gdlib?.closeWidget(moduleName);
      }; // Exit if the widget was started

    // If the widget is not yet available, set up a MutationObserver to wait for changes in the DOM
    const observer = new MutationObserver(() => {
      // Check again if the widget can be started
      if (startWidget()) {
        observer.disconnect(); // Stop observing once the widget is successfully started
      }
    });

    // Start observing for changes in the DOM
    observer.observe(document.body, { childList: true, subtree: true });

    // Cleanup function to be called on component unmount
    return () => {
      // Close the widget when the component is unmounted
      window.__gdlib?.closeWidget(moduleName);
      observer.disconnect(); // Clean up the observer to prevent memory leaks
    };
  }, [isLoaded, isFsRace]); // Empty dependency array means this effect runs once on mount and cleanup on unmount

  useEffect(() => {
    if (!isLoaded) return;
    window.__gdlib?.registerCallBack('navigateToCategory', 'race', (params: { category: string }) => {
      if (user) {
        _setSelectedCategory(params.category);
        navigate(`/category/${params.category}`);
      }
    });

    window.__gdlib?.registerCallBack('scrollLock', 'race', (value: 'set' | 'unset') => {
      setBodyOverflow(value ? 'set' : 'unset');
    });

    // Cleanup function to unregister the callbacks when the component unmounts or the effect re-runs
    return () => {
      // Unregister the callback for 'navigateToCategory'
      window.__gdlib?.unRegisterCallBack('navigateToCategory', 'race');
    };
  }, [isLoaded, user]);

  // Render a div element with the specified ID for the widget
  return <div className="race fullWidth page_content" id="raceWidgetId" />;
};

export default Race;
